<template>
  <BaseProductPage 
    :productType="productData.type"
  >
    <template #sidebar-nav>
      <li v-for="(section, index) in productData.sections" :key="index">
        <a
          :href="'#' + section.id"
          class="sidebar-nav-item"
        >{{ section.title }}</a>
      </li>
    </template>
    
    <template #content>
      <!-- Introduction Section -->
      <div :id="productData.sections[0].id">
        <ProductPageIntro
          v-if="!customIntro"
          :title="productData.introData.title"
          :paragraphs="productData.introData.paragraphs"
        />
        <slot v-else name="custom-intro"></slot>
      </div>

      <!-- Types of Insurance / List Section (optional) -->
      <div v-if="productData.typesList && productData.typesList.length" class="mb-6">
        <div class="bg-[#EDEDED] p-6 rounded-md">
          <h2
            class="xl:text-[20px] lg:text-[15px] text-[15px] pt-[10px] font-roboto font-bold tracking-[-0.2px] pb-[20px] text-[#1F2937]"
          >
            {{ productData.typesListTitle || `Different Types of ${productData.type} Insurance` }}
          </h2>
          <ul class="list-disc ml-12 text-red-600 text-3xl leading-7">
            <li v-for="(item, index) in productData.typesList" :key="index">
              <span
                class="text-[#1F2937] font-roboto font-regular text-[15px]"
                >{{ item }}</span
              >
            </li>
          </ul>
        </div>
        <hr class="w-full mt-6 h-[3px] bg-gray-300" />
      </div>

      <!-- Best Rate Tips Section -->
      <div :id="getSection('htgbr')?.id || 'htgbr'">
        <BestRateTips :tips="productData.bestRateTips" />
      </div>

      <!-- Brief Guide Section -->
      <div class="hidden md:block">
        <div>
          <h2
            class="xl:text-[35px] lg:text-[25px] text-[25px] pt-[10px] font-Merriweather font-black tracking-[-0.2px] pb-[20px] text-[#1F2937]"
          >
            Understanding {{ productData.type }} Insurance: A Brief Guide
          </h2>
          <p class="text-[15px] my-[15px] leading-[27px]">
            {{ productData.briefGuide }}
          </p>
        </div>

        <!-- Coverage Section -->
        <div :id="getSection('coverage')?.id || 'coverage'">
          <ProductPageCoverage
            :product-type="productData.type"
            :coverage-items="productData.coverageItems"
          />
        </div>
        
        <!-- Additional Info Sections -->
        <div v-if="productData.additionalInfo">
          <p class="text-[15px] my-[15px] font-regular leading-[27px] mb-2">
            {{ productData.additionalInfo.overview }}
          </p>
          
          <template v-for="(section, sectionIndex) in productData.additionalInfo.sections" :key="sectionIndex">
            <p class="text-[15px] my-[15px] font-bold leading-[27px]">
              {{ section.title }}
            </p>
            <p v-for="(paragraph, paragraphIndex) in section.paragraphs" 
               :key="paragraphIndex" 
               class="text-[15px] my-[15px] font-regular leading-[27px] mb-2">
              {{ paragraph }}
            </p>
          </template>
        </div>

        <!-- Custom Additional Sections (for specialized product content) -->
        <slot name="custom-sections"></slot>
      </div>

      <!-- Pricing Factors Section -->
      <section class="section">
        <div :id="getSection(productData.type.toLowerCase() + '2')?.id || productData.type.toLowerCase() + '2'">
          <h2 class="product-heading">
            {{ productData.pricingFactors.title }}
          </h2>
          <p class="product-paragraph">
            {{ productData.pricingFactors.description }}
          </p>
        </div>
        
        <!-- Pricing Factor Items -->
        <div v-for="(factor, index) in productData.pricingFactors.factors" :key="index" class="product-factor group">
          <div class="product-factor-icon">
            <img :src="getFactorImageUrl(factor.imageUrl)" alt="" />
          </div>
          <div class="product-factor-content">
            <h3 class="product-factor-title">
              {{ factor.title }}
            </h3>
            <p class="product-factor-description">
              {{ factor.description }}
            </p>
          </div>
        </div>
      </section>

      <!-- Related Products Section -->
      <div class="related-products-grid">
        <div v-for="(product, index) in productData.relatedProducts" :key="index" class="related-product-item">
          <div class="related-product-card group">
            <div class="related-product-content">
              <img
                :src="product.imageUrl"
                alt=""
                class="related-product-image"
              />
              <h3 class="related-product-title">
                {{ product.title }}
              </h3>
              <div>
                <a
                  :href="product.link"
                  class="related-product-link"
                  >Learn more</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CTA Section -->
      <div class="product-cta-section">
        <div class="product-cta-container">
          <div class="bg-[url('@/assets/images/productPageImages/watch-bg.png')] lg:bg-contain bg-[length:395px] bg-no-repeat bg-right-bottom">
            <div class="product-cta-content">
              <h2 class="product-cta-heading">
                WATCH YOUR SAVINGS ADD UP.
              </h2>
              <p class="product-cta-text">
                Always check to see if you can save money by adding an
                additional product to your {{ productData.type.toLowerCase() }} insurance policy! 
                {{ productData.ctaText || `(Home, Auto, Life, etc.)` }}
              </p>
              <div class="product-cta-button-container">
                <a
                  href="/chooseproducts"
                  class="product-cta-button"
                  >Get a quote
                  <svg
                    width="24px"
                    height="24px"
                    viewBox="0 0 24 24"
                    class="product-cta-button-icon"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M9.46967 5.46967C9.76256 5.17678 10.2374 5.17678 10.5303 5.46967L16.5303 11.4697C16.8232 11.7626 16.8232 12.2374 16.5303 12.5303L10.5303 18.5303C10.2374 18.8232 9.76256 18.8232 9.46967 18.5303C9.17678 18.2374 9.17678 17.7626 9.46967 17.4697L14.9393 12L9.46967 6.53033C9.17678 6.23744 9.17678 5.76256 9.46967 5.46967Z"
                      fill="#ffffff"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseProductPage>
</template>

<script>
import BaseProductPage from '@/components/products/BaseProductPage.vue';
import ProductPageIntro from '@/components/products/ProductPageIntro.vue';
import ProductPageHowTo from '@/components/products/ProductPageHowTo.vue';
import BestRateTips from '@/components/products/BestRateTips.vue';
import ProductPageCoverage from '@/components/products/ProductPageCoverage.vue';

// Import pricing factor images
import multiPolicyImg from '@/assets/images/productPageImages/multi.png';
import creditImg from '@/assets/images/productPageImages/credit.png';
import maritalImg from '@/assets/images/productPageImages/marital.png';
import ageImg from '@/assets/images/productPageImages/age.png';
import bookImg from '@/assets/images/productPageImages/book.png';

// Image mapping for factor images
const imageMapping = {
  '/images/productPageImages/multi.png': multiPolicyImg,
  '/images/productPageImages/credit.png': creditImg,
  '/images/productPageImages/marital.png': maritalImg,
  '/images/productPageImages/age.png': ageImg,
  '/images/productPageImages/book.png': bookImg,
  '@/assets/images/productPageImages/multi.png': multiPolicyImg,
  '@/assets/images/productPageImages/credit.png': creditImg,
  '@/assets/images/productPageImages/marital.png': maritalImg,
  '@/assets/images/productPageImages/age.png': ageImg,
  '@/assets/images/productPageImages/book.png': bookImg
};

export default {
  name: 'GenericProductPage',
  components: {
    BaseProductPage,
    ProductPageIntro,
    ProductPageHowTo,
    BestRateTips,
    ProductPageCoverage
  },
  props: {
    productData: {
      type: Object,
      required: true
    },
    customIntro: {
      type: Boolean,
      default: false
    }
  },
  computed: {
  },
  methods: {
    getSection(id) {
      return this.productData.sections.find(section => section.id === id || section.id.includes(id));
    },
    getFactorImageUrl(imageUrl) {
      // Check if we have a direct mapping for this image URL
      if (imageMapping[imageUrl]) {
        return imageMapping[imageUrl];
      }
      
      // Default image fallbacks based on common patterns
      if (imageUrl.includes('multi')) {
        return multiPolicyImg;
      } else if (imageUrl.includes('credit')) {
        return creditImg;
      } else if (imageUrl.includes('marital') || imageUrl.includes('age') || imageUrl.includes('demographic')) {
        return maritalImg;
      } else if (imageUrl.includes('book') || imageUrl.includes('education')) {
        return bookImg;
      }
      
      // Default fallback if no match is found
      return multiPolicyImg;
    }
  }
};
</script>

<style scoped>
/* Any shared styles specific to the generic product page */
</style> 